import React, { useContext, useState } from 'react';
import useTranslation from '../../useTranslation';
import { useClassNameMapper } from 'react-bootstrap';
import type { Form, SurveyQuestion } from '@aurora/shared-generated/types/graphql-schema-types';
import localStyles from './SurveyForm.module.pcss';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import ModalFormBuilder from '@aurora/shared-client/helpers/modal/ModalFormBuilder/ModalFormBuilder';
import surveyFormSchema from './SurveyForm.form.json';
import type { FilteredSurveyWithFinalQuestionCountAndProperties } from '@aurora/shared-client/components/surveys/UseSurvey/useSurvey';
import type { RadioFieldSpec } from '@aurora/shared-client/components/form/RadioField/RadioField';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import type {
  CreateSurveyResponseMutation,
  CreateSurveyResponseMutationVariables
} from '../../../types/graphql-types';
import createSurveyResponseMutation from '../CreateSurveyResponse.mutation.graphql';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import ToastContext from '@aurora/shared-client/components/context/ToastContext/ToastContext';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type { PossibleValueType } from '@aurora/shared-client/components/form/types';
import type { FormFieldVariant } from '@aurora/shared-client/components/form/enums';
import useSurveyQuestionsAndAnswersText from '@aurora/shared-client/components/surveys/UseSurveyQuestionsAndAnswersText/useSurveyQuestionsAndAnswersText';

type SurveyFormData = {
  purpose: string;
  helpful: string;
  yes_then: string;
  no_then: string;
  purchase: string;
  overall: string;
  recommend: string;
  feedback: string;
};

interface Props {
  /**
   * Callback to be used when the form is submitted
   */
  onSubmit: () => void;
  /**
   * Callback to be used when the form is cancelled
   */
  onCancel: () => void;
  /**
   * Computed survey to be displayed in the form
   */
  computedSurvey: FilteredSurveyWithFinalQuestionCountAndProperties;
  /**
   * Current step of the survey
   */
  step: number;
  /**
   * Callback to be used when the step is changed
   * @param step The new step to be set
   */
  onStepChange?: (step: number) => void;
}

/**
 * Value survey form component
 *
 * Adam Ayres
 */
const SurveyForm: React.FC<Props> = ({
  onSubmit,
  onCancel,
  computedSurvey,
  step,
  onStepChange
}) => {
  const cx = useClassNameMapper(localStyles);
  const [stepHasValue, setStepHasValue] = useState(false);
  const i18n = useTranslation(EndUserComponent.SURVEY_FORM);
  const {
    formatMessageForQuestion,
    formatMessageForAnswer,
    i18n: { loading: questionAndAnswerTextLoading }
  } = useSurveyQuestionsAndAnswersText();
  const { loading: textLoading, formatMessage } = i18n;
  const [createSurveyResponse] = useMutationWithTracing<
    CreateSurveyResponseMutation,
    CreateSurveyResponseMutationVariables
  >(module, createSurveyResponseMutation);
  const { contextNode } = useContext(AppContext);
  const { addToast } = useContext(ToastContext);

  const formSchema = surveyFormSchema as Form;

  if (textLoading || questionAndAnswerTextLoading) {
    return null;
  }

  function radiosForQuestion(
    questionId: keyof SurveyFormData
  ): PossibleValueType<FormFieldVariant.RADIO, keyof SurveyFormData, SurveyFormData> {
    const question: SurveyQuestion = computedSurvey.questions.find(({ id }) => id === questionId);
    if (question) {
      return question.answers
        .map(({ id }) => id)
        .map(key => ({
          key,
          value: key,
          label: formatMessageForAnswer(question.id, key, computedSurvey)
        }));
    }
    return [];
  }

  function fieldClassName(fieldNumber: number): string {
    return cx('lia-form-field', {
      'lia-active': step === fieldNumber
    });
  }

  const builder = new ModalFormBuilder<SurveyFormData>(
    'SurveyForm',
    i18n,
    {
      schema: formSchema,
      cx
    },
    {
      formClassName: cx('lia-form')
    }
  );

  function surveyHasQuestion(questionId: keyof SurveyFormData): boolean {
    return computedSurvey.questions.some(({ id }) => id === questionId);
  }

  function addRadioField(
    id: keyof SurveyFormData,
    questionStep: number,
    options?: Partial<Omit<RadioFieldSpec<keyof SurveyFormData, SurveyFormData>, 'fieldVariant'>>
  ): void {
    if (surveyHasQuestion(id)) {
      builder.addRadioField({
        name: id,
        defaultValue: '',
        values: radiosForQuestion(id),
        className: fieldClassName(questionStep),
        onChange() {
          setStepHasValue(true);
        },
        validations: {
          required: { value: step === questionStep, message: formatMessage('requiredQuestion') }
        },
        formGroupSpec: {
          label: {
            labelTextOverride: formatMessageForQuestion(id, computedSurvey)
          }
        },
        ...options
      });
    }
  }

  addRadioField('purpose', 1);
  addRadioField('helpful', 2);
  addRadioField('yes_then', 3, {
    isVisible: {
      watchFields: ['helpful'],
      callback: (formData: SurveyFormData) => formData.helpful === 'yes'
    }
  });
  addRadioField('no_then', 3, {
    isVisible: {
      watchFields: ['helpful'],
      callback: (formData: SurveyFormData) => formData.helpful === 'no'
    }
  });
  addRadioField('purchase', 4);
  addRadioField('overall', 5);

  builder.addRadioWithScaleField({
    name: 'recommend',
    defaultValue: '',
    className: fieldClassName(6),
    onChange() {
      setStepHasValue(true);
    },
    validations: {
      required: { value: step === 6, message: formatMessage('requiredQuestion') }
    }
  });

  builder.addTextAreaField({
    name: 'feedback',
    defaultValue: '',
    className: fieldClassName(7)
  });

  builder
    .addFormAction({
      actionId: 'submit',
      buttonSpec: {
        title: step < computedSurvey.finalQuestionCount ? formatMessage('next') : null
      }
    })
    .addCancelAction();

  const formSpec = builder.build();

  function handleSubmit(formData: SurveyFormData, actionId: string) {
    if (actionId === 'cancel') {
      onCancel();
    } else if (actionId === 'submit') {
      if (step < computedSurvey.finalQuestionCount) {
        if (stepHasValue) {
          onStepChange(step + 1);
          setStepHasValue(false);
        }
      } else {
        createSurveyResponse({
          variables: { nodeId: contextNode.id, values: formData }
        });

        onSubmit();
        addToast({
          toastVariant: ToastVariant.FLYOUT,
          alertVariant: ToastAlertVariant.SUCCESS,
          title: formatMessage('successTitle'),
          message: formatMessage('successMessage'),
          autohide: true,
          id: `survey-response-${contextNode.id}-${computedSurvey.id}`
        });
      }
    }
  }

  return <InputEditForm formSpec={formSpec} onSubmit={handleSubmit} />;
};

export default SurveyForm;
